/**
 * OO «Зарплаты» — per-period salary ledger (V1 screens_OO/290675–683 → V4).
 * Sub-tab switcher Зарплаты | Ставки. Зарплаты: KPI (ВЫРУЧКА/ФОТ/НЕТТО/
 * ИГР·ОТЧЁТОВ/РАСХОЖДЕНИЯ) → operator ledger with inline payslip breakdown →
 * sticky footer (Отправить ЗП в ТГ / Расчётки overlay). Ставки: the rate grid
 * (read-only mirror unless `ratesEditable`).
 *
 * RBAC: OO/EO only (route-gated by the shell). Salary maths is backend-side;
 * we render + send. Rate grid is EO-owned (A16); OO edits it until EO ships.
 * 04.09 (владелец): покупки операторов — компенсация в составе выплаты, лайм var(--lime).
 * 05.09 (владелец): премии (bonus_total / days[d].bonuses — уже в типах useSalary) — синим var(--blue).
 */
import { Fragment, useMemo, useState } from "react";
import {
  useSalary, type OperatorSalary, type SalaryDay, type PeriodKey, type SalaryRange,
} from "../../hooks/useSalary";
import { ChevDown, ActSpin, ActDopWork, Diam, McStar } from "./v8icons";
import { Taxi, Mask, Gear, Receipt, Coin, Envelope } from "./finIcons";
import { RatesGrid } from "./RatesGrid"; import { PlayersBadge } from "../../components/shared"; import { EwPayNote, EwPayNoteRow } from "./EwPayNote"; import { PAID_1C_LABEL, PAID_1C_SHORT, TAXI_1C_LABEL } from "@ub/shared/archive1c";   /* 15.10 (владелец): под допработой — за что заплачено; 13.10 архив 1С (ПР3): выплачено через 1С — подпись, вне итога */ import { SlipMarkTag, slipRowClass } from "../../components/shared/SlipMark";   // 26.10: отметка в расчётке
import css from "./salary.module.css";

/* ── formatting ──────────────────────────────────────────────────────────── */
const rub = (n: number) => `₽${Math.round(n || 0).toLocaleString("ru-RU")}`;
const DOW = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
function dm(iso: string): string { const [, m, d] = iso.split("-"); return `${d}.${m}`; }
function dayHead(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dow = DOW[new Date(y!, (m! - 1), d!).getDay()] ?? "";
  return `${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")} (${dow})`;
}
const penaltyCount = (op: OperatorSalary): number => Object.values(op.days).reduce((n, day) => n + (day.penalties?.length ?? 0), 0);
/* 04.09: покупки (purchase_total / days[d].purchases) приходят в том же salary.staff; useSalary не трогаем — локальное сужение */
interface BuyLine { id?: number; amount: number; description?: string; comment?: string | null }
type DayX = SalaryDay & { purchases?: BuyLine[] }; type OpX = OperatorSalary & { purchase_total?: number; days: Record<string, DayX> };

const PERIODS: { key: PeriodKey; label: string }[] = [{ key: "previous", label: "ПР.НЕД" }, { key: "current", label: "ТЕК.НЕД" }, { key: "month", label: "МЕСЯЦ" }];

export function OoSalaryView({ ratesEditable = false, initialTab = "salary" }: { ratesEditable?: boolean; initialTab?: "salary" | "rates" } = {}) {
  const sal = useSalary();
  const [tab, setTab] = useState<"salary" | "rates">(initialTab);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [receiptsOpen, setReceiptsOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const toggle = (id: string) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const fotPayable = useMemo(() => sal.operators.filter((o) => o.total > 0), [sal.operators]);

  async function doSend() {
    setConfirm(false);
    const res = await sal.notifySalary();
    setToast(res.ok ? `Отправлено зарплат: ${res.sent ?? 0}` : `Ошибка: ${res.error ?? "—"}`);
    setTimeout(() => setToast(null), 3200);
  }

  return (
    <div className={css.wrap}>
      {/* sub-tab switcher */}
      <div className={css.subtabs}>
        <button type="button" className={`${css.subtab} ${tab === "salary" ? css.subtabOn : ""}`} onClick={() => setTab("salary")}><Coin aria-hidden="true" /> Зарплаты</button>
        <button type="button" className={`${css.subtab} ${tab === "rates" ? css.subtabOn : ""}`} onClick={() => setTab("rates")}><Gear aria-hidden="true" /> Ставки</button>
      </div>

      {tab === "rates" ? (
        <RatesGrid readOnly={!ratesEditable} />
      ) : (
      <>
      {/* period */}
      <div className={css.periodRow}>
        <span className={css.periodLabel}>ПЕРИОД</span>
        <div className={css.periodChips}>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`${css.chip} ${sal.period === p.key ? css.chipOn : ""}`}
              onClick={() => sal.setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
          <button type="button" className={css.iconBtn} aria-label="Настроить период"><Gear aria-hidden="true" /></button>
          <button type="button" className={css.iconBtn} aria-label="Обновить" onClick={sal.reload}><ActSpin aria-hidden="true" /></button>
        </div>
      </div>
      {!!sal.range && <div className={css.range}>{dm(sal.range.from)} — {dm(sal.range.to)}</div>}

      {sal.loading ? (
        <LedgerSkeleton />
      ) : sal.error ? (
        <div className={css.empty}>{sal.error}<br /><button type="button" className={css.retry} onClick={sal.reload}>Повторить</button></div>
      ) : (
        <>
          {/* KPI */}
          {!!sal.kpi && (
            <>
              <div className={css.kpiBig}>
                <div className={css.kpiCard}>
                  <span className={css.kpiLabel}>ВЫРУЧКА</span>
                  <span className={`${css.kpiNum} ${css.green}`}>{rub(sal.kpi.revenue)}</span>
                  <span className={css.kpiSub}>нал {rub(sal.kpi.cash)} · безнал {rub(sal.kpi.terminal)} · онлайн {rub(sal.kpi.transfer)}</span>
                </div>
                <div className={css.kpiCard}>
                  <span className={css.kpiLabel}>ФОТ</span>
                  <span className={`${css.kpiNum} ${css.orange}`}>{rub(sal.kpi.fot)}</span>
                  <span className={css.kpiSub}>квесты {rub(sal.kpi.fotQuests)} · доп {rub(sal.kpi.fotExtra)} · админ {rub(sal.kpi.fotHall)}</span>
                </div>
              </div>
              <div className={css.kpiSmall}>
                <div className={css.kpiCardS}>
                  <span className={css.kpiLabel}>НЕТТО</span>
                  <span className={`${css.kpiNumS} ${css.green}`}>{rub(sal.kpi.netto)}</span>
                </div>
                <div className={css.kpiCardS}>
                  <span className={css.kpiLabel}>ОТЧЁТОВ/ИГР</span>
                  <span className={`${css.kpiNumS} ${css.cyan}`}>{sal.kpi.reports}/{sal.kpi.games}</span>
                </div>
                <div className={css.kpiCardS}>
                  <span className={css.kpiLabel}>РАСХОЖДЕНИЯ</span>
                  <span className={`${css.kpiNumS} ${sal.kpi.discrepancies > 0 ? css.red : css.green}`}>{sal.kpi.discrepancies || "✓"}</span>
                </div>
              </div>
            </>
          )}

          {/* ledger */}
          <div className={css.sectionLabel}>ЗАРПЛАТЫ ОПЕРАТОРОВ</div>
          {sal.operators.length === 0 ? (
            <div className={css.empty}>За период начислений нет</div>
          ) : (
            <ul className={css.ledger}>
              {sal.operators.map((op) => {
                const fines = penaltyCount(op);
                const isOpen = open.has(op.staffId);
                return (
                  <li key={op.staffId} className={css.row}>
                    <button type="button" className={css.rowHead} onClick={() => toggle(op.staffId)} aria-expanded={isOpen}>
                      <span className={css.rowName}>{op.name}</span>
                      <span className={css.rowGames}>{op.games_count} игр</span>
                      {fines > 0 && (
                        <span className={css.fine} title={`Штрафов: ${fines}`}><ActDopWork aria-hidden="true" />{fines}</span>
                      )}
                      <span className={`${css.rowSum} ${css.green}`}>{rub(op.total)}</span>
                      <span className={`${css.rowChev} ${isOpen ? css.chevOpen : ""}`}><ChevDown aria-hidden="true" /></span>
                    </button>
                    {isOpen && <OperatorBreakdown op={op as OpX} />}
                  </li>
                );
              })}
            </ul>
          )}
          <div className={css.footSpacer} />
        </>
      )}

      {/* sticky footer */}
      <div className={css.footer}>
        <button type="button" className={`${css.cta} ${css.ctaSend}`} disabled={fotPayable.length === 0} onClick={() => setConfirm(true)}>
          <Envelope aria-hidden="true" /> Отправить ЗП в ТГ
        </button>
        <button type="button" className={`${css.cta} ${css.ctaReceipts}`} disabled={sal.operators.length === 0} onClick={() => setReceiptsOpen(true)}>
          <Receipt aria-hidden="true" /> Расчётки
        </button>
      </div>

      {/* custom confirm (A3 — not native) */}
      {confirm && (
        <div className={css.modalBack} onClick={() => setConfirm(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalTitle}>Отправить зарплаты в ТГ?</div>
            <div className={css.modalBody}>
              {fotPayable.length} операторам на сумму <b className={css.green}>{rub(sal.kpi?.fot ?? 0)}</b> за период {sal.range ? `${dm(sal.range.from)} — ${dm(sal.range.to)}` : ""}.
            </div>
            <div className={css.modalActions}>
              <button type="button" className={css.btnGhost} onClick={() => setConfirm(false)}>Отмена</button>
              <button type="button" className={`${css.btnPrimary} ${css.ctaSend}`} onClick={doSend}>Отправить</button>
            </div>
          </div>
        </div>
      )}

      {receiptsOpen && (
        <ReceiptsOverlay operators={sal.operators} range={sal.range} fot={sal.kpi?.fot ?? 0} onClose={() => setReceiptsOpen(false)} onSend={() => { setReceiptsOpen(false); setConfirm(true); }} />
      )}
      </>
      )}

      {!!toast && <div className={css.toast}>{toast}</div>}
    </div>
  );
}

/* ── per-operator inline payslip breakdown ───────────────────────────────── */
function OperatorBreakdown({ op }: { op: OpX }) {
  const days = Object.keys(op.days).sort();
  return (
    <div className={css.breakdown}>
      <div className={css.cats}>
        {op.quest_earnings > 0 && <span className={css.cat}><Mask aria-hidden="true" /> квесты {rub(op.quest_earnings)}</span>}
        {op.mc_earnings > 0 && <span className={`${css.cat} ${css.catMc}`}><McStar aria-hidden="true" /> МК {rub(op.mc_earnings)}</span>}
        {op.hall_admin_earnings > 0 && <span className={css.cat}><Diam aria-hidden="true" /> админ {rub(op.hall_admin_earnings)}</span>}
        {op.extra_work_earnings > 0 && <span className={css.cat}><ActDopWork aria-hidden="true" /> доп {rub(op.extra_work_earnings)}</span>}
        {op.taxi_earnings > 0 && <span className={css.cat}><Taxi aria-hidden="true" /> такси {rub(op.taxi_earnings)}</span>}
        {(op.purchase_total ?? 0) > 0 && <span className={`${css.cat} ${css.catBuy}`}><Receipt aria-hidden="true" /> покупки {rub(op.purchase_total ?? 0)}</span>}
        {op.bonus_total > 0 && <span className={`${css.cat} ${css.catBonus}`}><Coin aria-hidden="true" /> премии {rub(op.bonus_total)}</span>}
        {op.penalty_total > 0 && <span className={`${css.cat} ${css.catFine}`}><ActDopWork aria-hidden="true" /> штраф −{rub(op.penalty_total)}</span>}{(op.paid_1c_total ?? 0) > 0 && <span className={css.cat}>{PAID_1C_LABEL} {rub(op.paid_1c_total ?? 0)} — не в итоге</span>}
      </div>
      {days.map((d) => <DayBlock key={d} iso={d} day={op.days[d]!} />)}
    </div>
  );
}

function DayBlock({ iso, day }: { iso: string; day: DayX }) {
  return (
    <div className={css.day}>
      <div className={css.dayHead}>
        <span>{dayHead(iso)}</span>
        <span className={css.green}>{rub(day.day_total)}</span>
      </div>
      {day.quests.map((q, i) => (
        <div key={`q${i}`} className={css.line}>
          <span className={css.lineMain}>
            <span className={css.lineQuest}>{q.short_name || q.quest_name}</span>
            <span className={css.lineMeta}>{q.time} · <PlayersBadge count={q.players} size="sm" />{q.activity ? ` · ${q.activity}` : ""}{q.paid_1c ? ` · ${PAID_1C_SHORT}` : ""}{q.taxi_1c && q.taxi_amount > 0 ? ` · ${TAXI_1C_LABEL} ${rub(q.taxi_amount)}` : ""}</span>{/* 05.09 (владелец): бейдж игроков без брони — индикатор */}
          </span>
          {q.edits && q.edits.count > 0 && <span className={css.override}>✎{q.edits.count}</span>}
          <span className={`${css.lineRate} ${q.edits && q.edits.count > 0 ? css.pink : ""}`}>{rub(q.rate)}</span>
        </div>
      ))}
      {day.mc.map((m, i) => (
        <div key={`m${i}`} className={css.line}>
          <span className={css.lineMain}><span className={css.lineQuest}>МК {m.mc_name}</span><span className={css.lineMeta}>{m.time}{m.paid_1c ? ` · ${PAID_1C_SHORT}` : ""}</span></span>
          <span className={css.lineRate}>{rub(m.rate)}</span>
        </div>
      ))}
      {!!day.hall_admin && (
        <div className={css.line}>
          <span className={css.lineMain}><span className={css.lineQuest}>Админ зала · {day.hall_admin.branch}</span><span className={css.lineMeta}>{day.hall_admin.time_from}—{day.hall_admin.time_to} · {(day.hall_admin as { fixed?: boolean }).fixed ? `${day.hall_admin.hours}ч · сумма задана вручную` : `${day.hall_admin.hours}ч × ${rub(day.hall_admin.rate)}`}</span></span>
          <span className={css.lineRate}>{rub(day.hall_admin.earning)}</span>
        </div>
      )}
      {(day.extra_work ?? []).map((ew, i) => (
        <div key={`e${i}`} className={css.line}>
          <span className={css.lineMain}><span className={css.lineQuest}>{ew.description}</span><span className={css.lineMeta}>{ew.time_from}—{ew.time_to}{ew.taxi > 0 ? ` · такси ${rub(ew.taxi)}` : ""}</span><EwPayNote e={ew} /></span>
          {ew.rate_override != null && <span className={css.override}>override</span>}
          <span className={`${css.lineRate} ${ew.rate_override != null ? css.pink : ""}`}>{rub(ew.earning)}</span>
        </div>
      ))}
      {(day.purchases ?? []).map((b, i) => (
        <div key={`b${i}`} className={`${css.line} ${css.lineBuy}`}><span className={css.lineMain}><span className={css.lineQuest}>Покупка · компенсация</span><span className={css.lineMeta}>{b.description}{b.comment ? ` · ${b.comment}` : ""}</span></span><span className={css.lineRate}>{rub(b.amount)}</span></div>
      ))}
      {(day.bonuses ?? []).map((n, i) => (
        <div key={`n${i}`} className={`${css.line} ${css.lineBonus} ${slipRowClass(n.mark, "bonus")}`}><span className={css.lineMain}><span className={css.lineQuest}>Премия<SlipMarkTag mark={n.mark} tone="bonus" /></span><span className={css.lineMeta}>{n.reason}</span></span><span className={css.lineRate}>{rub(n.amount)}</span></div>
      ))}
      {(day.penalties ?? []).map((p, i) => (
        <div key={`p${i}`} className={`${css.line} ${css.lineFine} ${slipRowClass(p.mark, "pen")}`}>
          <span className={css.lineMain}><span className={css.lineQuest}>Штраф<SlipMarkTag mark={p.mark} tone="pen" /></span><span className={css.lineMeta}>{p.reason}</span></span>
          <span className={`${css.lineRate} ${css.red}`}>−{rub(p.amount)}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Расчётки overlay (C13 full-screen) ──────────────────────────────────── */
function ReceiptsOverlay({ operators, range, fot, onClose, onSend }: {
  operators: OperatorSalary[]; range: SalaryRange | null; fot: number; onClose: () => void; onSend: () => void;
}) {
  const payable = operators.filter((o) => o.total > 0);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(payable.map((o) => o.staffId)));
  const all = () => setPicked(new Set(payable.map((o) => o.staffId))); const none = () => setPicked(new Set()); const tog = (id: string) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className={css.ovBack}>
      <div className={css.ovHead}>
        <span className={css.ovTitle}><Receipt aria-hidden="true" /> РАСЧЁТКИ ({picked.size}/{payable.length})</span>
        <div className={css.ovHeadBtns}>
          <button type="button" className={css.ovSel} onClick={all}>Все</button>
          <button type="button" className={css.ovSel} onClick={none}>Ни одной</button>
          <button type="button" className={css.ovClose} aria-label="Закрыть" onClick={onClose}>✕</button>
        </div>
      </div>
      <div className={css.ovBody}>
        {payable.map((op) => (
          <label key={op.staffId} className={css.ovRow}>
            <input type="checkbox" className={css.ovCheck} checked={picked.has(op.staffId)} onChange={() => tog(op.staffId)} />
            <PayslipFrame op={op as OpX} range={range} />
          </label>
        ))}
      </div>
      <div className={css.ovFooter}>
        <span className={css.ovSum}>Итого к выплате: <b className={css.green}>{rub(fot)}</b></span>
        <button type="button" className={`${css.btnPrimary} ${css.ctaSend}`} disabled={picked.size === 0} onClick={onSend}>
          <Envelope aria-hidden="true" /> Отправить в ТГ
        </button>
      </div>
    </div>
  );
}

function PayslipFrame({ op, range }: { op: OpX; range: SalaryRange | null }) {
  const days = Object.keys(op.days).sort();
  return (
    <div className={css.payslip}>
      <span className={`${css.psCorner} ${css.psTl}`} aria-hidden="true" />
      <span className={`${css.psCorner} ${css.psBr}`} aria-hidden="true" />
      <div className={css.psHero}>
        <div>
          <div className={css.psName}>{op.name}</div>
          <div className={css.psPeriod}>{range ? `${range.from} — ${range.to}` : ""} · РАСЧЁТНЫЙ ЛИСТ</div>
        </div>
        <div className={`${css.psTotal} ${css.green}`}>{rub(op.total)}<span className={css.psGames}>{op.games_count} игр</span></div>
      </div>
      <table className={css.psTable}>
        <thead><tr><th>ДАТА</th><th>ВРЕМЯ</th><th>КВЕСТ / ЧТО ДЕЛАЛ</th><th className={css.psR}>СТАВКА</th></tr></thead>
        <tbody>
          {days.flatMap((d) => [
            ...op.days[d]!.quests.map((q, i) => (
              <tr key={`${d}q${i}`}><td>{dm(d)}</td><td>{q.time}</td><td className={css.psWhat}>{q.short_name || q.quest_name}{q.paid_1c ? ` · ${PAID_1C_SHORT}` : ""}</td><td className={css.psR}>{rub(q.rate)}</td></tr>
            )),
            ...(op.days[d]!.extra_work ?? []).map((ew, i) => (
              <Fragment key={`${d}e${i}`}><tr><td>{dm(d)}</td><td>{ew.time_from}</td><td className={css.psWhat}>{ew.description}</td><td className={css.psR}>{rub(ew.earning)}</td></tr><EwPayNoteRow e={ew} span={3} /></Fragment>
            )),
            ...(op.days[d]?.purchases ?? []).map((b, i) => (
              <tr key={`${d}b${i}`}><td>{dm(d)}</td><td /><td className={`${css.psWhat} ${css.lime}`}>Покупка · {b.description}</td><td className={`${css.psR} ${css.lime}`}>{rub(b.amount)}</td></tr>
            )),
            ...(op.days[d]?.bonuses ?? []).map((n, i) => (
              <tr key={`${d}n${i}`}><td>{dm(d)}</td><td /><td className={`${css.psWhat} ${css.blue}`}>Премия<SlipMarkTag mark={n.mark} tone="bonus" /> · {n.reason}</td><td className={`${css.psR} ${css.blue}`}>{rub(n.amount)}</td></tr>
            )),
          ])}
        </tbody>
      </table>
      <div className={css.psTotals}>
        {op.quest_earnings > 0 && <span>Квесты {rub(op.quest_earnings)}</span>}
        {op.extra_work_earnings > 0 && <span>Доп {rub(op.extra_work_earnings)}</span>}
        {op.taxi_earnings > 0 && <span>Такси {rub(op.taxi_earnings)}</span>}
        {(op.purchase_total ?? 0) > 0 && <span className={css.lime}>Покупки {rub(op.purchase_total ?? 0)}</span>}
        {op.bonus_total > 0 && <span className={css.blue}>Премии {rub(op.bonus_total)}</span>}
        {op.penalty_total > 0 && <span className={css.red}>Штраф −{rub(op.penalty_total)}</span>}{(op.paid_1c_total ?? 0) > 0 && <span>{PAID_1C_LABEL} {rub(op.paid_1c_total ?? 0)} — не в итоге</span>}
        <span className={`${css.psItogo} ${css.green}`}>ИТОГО {rub(op.total)}</span>
      </div>
    </div>
  );
}

function LedgerSkeleton() {
  return <div className={css.skel}><div className={css.skKpi} /><div className={css.skKpi} />{Array.from({ length: 6 }).map((_, i) => <div key={i} className={css.skRow} />)}</div>;
}
